import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, ChevronRight, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PropertyCardProps {
  property: {
    id: string;
    address: string;
    clientName?: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    tasksTotal: number;
    tasksCompleted: number;
    tasksRemaining: number;
    status: 'pending' | 'in_progress' | 'completed';
  };
  onClick: () => void;
}

const getPriorityConfig = (t: (key: string) => string) => ({
  low: { 
    label: t('priority.low'), 
    className: 'bg-[#EDF2F7] text-[#4A5568] dark:bg-[#374151] dark:text-white' 
  },
  medium: { 
    label: t('priority.medium'), 
    className: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400' 
  },
  high: { 
    label: t('priority.high'), 
    className: 'bg-[#FEEBC8] text-[#C05621] dark:bg-[#D97706] dark:text-white' 
  },
  urgent: { 
    label: t('priority.urgent'), 
    className: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400' 
  },
});

export const PropertyCard = ({ property, onClick }: PropertyCardProps) => {
  const { t } = useTranslation();
  const priorityConfig = getPriorityConfig(t);
  const priority = priorityConfig[property.priority];
  const isCompleted = property.status === 'completed';
  const progressPercent = property.tasksTotal > 0 
    ? Math.round((property.tasksCompleted / property.tasksTotal) * 100) 
    : 0;

  return (
    <Card 
      className={cn(
        "border-none shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer group",
        "bg-white dark:bg-[#1a2034]",
        "hover:scale-[1.01] hover:border-primary/20",
        isCompleted && "opacity-60"
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="h-5 w-5 text-primary dark:text-[#60a5fa] flex-shrink-0" />
              <h3 className="truncate text-lg font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                {property.address}
              </h3>
            </div>
            {property.clientName && (
              <p className="text-sm text-muted-foreground ml-6 mb-2">{property.clientName}</p>
            )}
            
            {/* Progress bar */}
            <div className="ml-6 flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-muted dark:bg-[#2d3748] rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full rounded-full transition-all",
                    progressPercent === 100 
                      ? "bg-green-500 dark:bg-green-400" 
                      : "bg-primary dark:bg-[#60a5fa]"
                  )}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span
                className={cn(
                  "text-xs whitespace-nowrap font-medium",
                  property.tasksTotal > 0 && property.tasksCompleted === property.tasksTotal
                    ? "text-green-600 dark:text-green-400"
                    : "text-muted-foreground"
                )}
              >
                {property.tasksCompleted}/{property.tasksTotal}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isCompleted ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <>
                <Badge variant="secondary" className={cn("font-medium text-xs px-2 py-0.5 rounded-md", priority.className)}>
                  {priority.label}
                </Badge>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary dark:group-hover:text-[#60a5fa] transition-colors" />
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
